import { ZeroconfResult } from '@awesome-cordova-plugins/zeroconf';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { Back, ConnectionStatus, Home, IconButton, Logo } from '@mgh-app/component-library';
import { useAuth } from 'auth/useAuth';
import { Title } from 'components/Title';
import { AnimatePresence, motion } from 'framer-motion';
import { useAppNavigation } from 'navigation/useAppNavigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useOutlet } from 'react-router-dom';
import { POLLING_INTERVAL, startWatchingZeroConf, stopWatchingZeroConf } from 'services/apiConfig';
import { useGetApiStatusQuery } from 'services/StatusService';
import { useGetAirQualityAllSensorsQuery, useGetEnergyValuesQuery } from 'services/ThingsService';
import {
  changeLocalConnectionStatus,
  changeNetworkStatus,
  getNetworkStatus,
  localEdgeIPAddress,
  localEdgeIPAddressMissing,
  localEdgeIPAddressPingFailed,
} from 'slices/NetworkSlice';
import { useAppDispatch, useAppSelector } from 'store';
import {
  borderRadius,
  borderStyle,
  borderWidth,
  combineCls,
  display,
  flexDirection,
  gridTemplateRows,
  height,
  inset,
  justifyContent,
  margin,
  overflow,
  padding,
  position,
  textAlign,
  textColor,
  twCls,
  width,
} from 'style';

export interface MainLayoutContextProps {
  setTitle: (title: string) => void;
  showNavigationButtons: (showNavigationButtons: boolean) => void;
  showUserProfile: (showUserProfile: boolean) => void;
  showHomeButton: (showHomeButton: boolean) => void;
  navigateHomeAction: (navigateHomeAction: () => void) => void;
  navigateBackAction: (navigateBackAction: () => void) => void;
}

export const MainLayoutContext = React.createContext<MainLayoutContextProps>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setTitle: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  showNavigationButtons: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  showUserProfile: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  navigateHomeAction: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  navigateBackAction: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  showHomeButton: () => {},
});

const AnimatedOutlet = () => {
  const [context] = useState(useOutlet());
  return <>{context}</>;
};

const animationVariants = {
  forward: { x: 0 },
  backward: { x: window.innerWidth },
};

const isAnimationEnabled = () => Capacitor.getPlatform() !== 'android';

export const MainLayout = () => {
  const { user } = useAuth();
  const { navigateBack, navigateHome, isHome } = useAppNavigation();
  const [title, setTitle] = useState<string>('');
  const [showNavigationButtons, setShowNavigationButtons] = useState<boolean>(true);
  const [showUserProfile, setShowUserProfile] = useState<boolean>(true);
  const [showHomeButton, setShowHomeButton] = useState<boolean>(true);
  const [isBackNavigation, setIsBackNavigation] = useState(false);
  const backFuncRef = useRef<() => void>(navigateBack);
  const wrapBackAction = (f: () => void) => () => {
    if (isAnimationEnabled()) {
      setIsBackNavigation(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      backFuncRef.current = f() as any;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (f() as any)();
    }
  };

  useEffect(() => {
    if (isBackNavigation) {
      backFuncRef.current();
    }
  }, [isBackNavigation]);

  const [navigateHomeAction, setNavigateHomeAction] = useState<() => void>(() => navigateHome);
  const [navigateBackAction, setNavigateBackAction] = useState<() => void>(() =>
    wrapBackAction(navigateBack),
  );
  const setWrappedNavigateBackAction = useCallback(
    (func: () => void) => setNavigateBackAction(() => wrapBackAction(func)),
    [setNavigateBackAction],
  );
  useGetApiStatusQuery(undefined, { pollingInterval: POLLING_INTERVAL });
  useGetAirQualityAllSensorsQuery(undefined, { pollingInterval: POLLING_INTERVAL });
  useGetEnergyValuesQuery(undefined);
  const dispatch = useAppDispatch();
  const networkStatus = useAppSelector(getNetworkStatus);
  const value: MainLayoutContextProps = {
    setTitle,
    showNavigationButtons: setShowNavigationButtons,
    showUserProfile: setShowUserProfile,
    showHomeButton: setShowHomeButton,
    navigateHomeAction: setNavigateHomeAction,
    navigateBackAction: setWrappedNavigateBackAction,
  };
  const location = useLocation();

  const onServiceStatusChange = useCallback(
    (result: ZeroconfResult) => {
      if (result.action === 'resolved') {
        dispatch(changeLocalConnectionStatus(true));
      } else if (result.action === 'removed') {
        dispatch(changeLocalConnectionStatus(false));
      }
    },
    [dispatch],
  );

  const onLocalEdgeIPAddress = useCallback(
    (address?: string) => dispatch(localEdgeIPAddress(address)),
    [dispatch],
  );
  const onLocalEdgeIPAddressMissing = useCallback(
    () => dispatch(localEdgeIPAddressMissing()),
    [dispatch],
  );
  const onLocalEdgeIPAddressPingFailed = useCallback(
    () => dispatch(localEdgeIPAddressPingFailed()),
    [dispatch],
  );

  useEffect(() => {
    App.addListener('backButton', () => {
      if (isHome()) {
        App.minimizeApp();
      } else {
        (navigateBackAction || navigateBack)();
      }
    });

    return () => {
      App.removeAllListeners();
    };
  }, [isHome, navigateBack, navigateBackAction]);

  useEffect(() => {
    (async () => {
      const status = await Network.getStatus();
      dispatch(changeNetworkStatus(status));

      if (status.connectionType === 'wifi') {
        startWatchingZeroConf(
          user?.unitId,
          onServiceStatusChange,
          onLocalEdgeIPAddress,
          onLocalEdgeIPAddressMissing,
          onLocalEdgeIPAddressPingFailed,
        );
      }
    })();

    Network.addListener('networkStatusChange', (status) => {
      dispatch(changeNetworkStatus(status));

      if (status.connectionType === 'wifi') {
        startWatchingZeroConf(
          user?.unitId,
          onServiceStatusChange,
          onLocalEdgeIPAddress,
          onLocalEdgeIPAddressMissing,
          onLocalEdgeIPAddressPingFailed,
        );
      } else {
        stopWatchingZeroConf();
      }
    });

    return () => {
      stopWatchingZeroConf();
      Network.removeAllListeners();
    };
  }, [
    dispatch,
    onLocalEdgeIPAddress,
    onLocalEdgeIPAddressMissing,
    onLocalEdgeIPAddressPingFailed,
    onServiceStatusChange,
    user?.unitId,
  ]);

  return (
    <MainLayoutContext.Provider value={value}>
      <div
        className={combineCls(
          twCls(display('grid'), gridTemplateRows('grid-rows-min-2-fc'), height('h-full')),
          'antialiased',
        )}
      >
        <div
          className={twCls(
            display('flex'),
            flexDirection('flex-row'),
            justifyContent('justify-between'),
          )}
        >
          <Logo />
          {showUserProfile && user && (
            <div className={twCls(display('flex'), flexDirection('flex-row'))}>
              {user.name}
              <img
                src={user.avatar}
                alt="User avatar"
                className={twCls(height('h-6'), width('w-6'), margin('ml-2'))}
              />
            </div>
          )}
        </div>
        <div className={twCls(position('relative'))}>
          {showNavigationButtons && (
            <>
              {showHomeButton && (
                <IconButton
                  className={twCls(position('absolute'), margin('mt-2'))}
                  onClick={navigateHomeAction}
                >
                  <Home />
                </IconButton>
              )}
              <IconButton
                className={combineCls(twCls(position('absolute'), margin('mt-2')), {
                  [twCls(margin('ml-7'))]: showHomeButton,
                })}
                onClick={navigateBackAction}
              >
                <Back />
              </IconButton>
            </>
          )}
          <Title>{title || <>&nbsp;</>}</Title>
          <ConnectionStatus
            className={twCls(position('absolute'), inset('top-3', 'right-0'), margin('mt-1'))}
            mobileConnected={networkStatus.mobileConnected}
            cloudConnected={networkStatus.cloudOnline}
            edgeConnected={networkStatus.edgeOnline}
          />
        </div>
        <div
          className={twCls(
            display('grid'),
            gridTemplateRows('grid-rows-1'),
            overflow('overflow-visible'),
          )}
        >
          {!networkStatus.mobileConnected && (
            <div className={twCls(display('flex'), flexDirection('flex-col'))}>
              <h1
                className={twCls(
                  display('inline-block'),
                  textAlign('text-center'),
                  margin('mt-4', 'mx-auto'),
                  textColor('text-blue-100'),
                  borderRadius('rounded-full'),
                  borderWidth('border'),
                  borderStyle('border-solid'),
                  padding('px-17', 'py-1'),
                )}
              >
                No Connection
              </h1>
            </div>
          )}
          {networkStatus.mobileConnected &&
            (isAnimationEnabled() ? (
              <AnimatePresence mode="wait">
                <motion.div
                  variants={animationVariants}
                  key={location.pathname}
                  exit={isBackNavigation ? 'backward' : undefined}
                  transition={{ duration: 0.3 }}
                  onAnimationComplete={() => setIsBackNavigation(false)}
                  className={twCls(
                    display('grid'),
                    gridTemplateRows('grid-rows-1'),
                    overflow('overflow-visible'),
                  )}
                >
                  <AnimatedOutlet />
                </motion.div>
              </AnimatePresence>
            ) : (
              <Outlet />
            ))}
        </div>
      </div>
    </MainLayoutContext.Provider>
  );
};
